import React from 'react';

import { type UUID, isDefined, isEmpty } from '@nfctron/nexus-core';
import { type ExtendedComponent, cn } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';

import { Gallery } from '@root/lib/components/Gallery/Gallery';

import { type CartStoreExtraProductsSlice } from '../../stores/cartStore';

/**
 * ProductCardBase component
 * @author filipditrich <ditrich@nfctron.com>
 */
export const ProductCardBase: React.FunctionComponent<ProductCardBaseProps> = ({
	title,
	gallery,
	actions,
	description,
	footer,
	price,
	type,
	className,
	...props
}) => (
	<div className={cn(['flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white', className])} {...props}>
		{/* gallery */}
		{isDefined(gallery) && <Gallery gallery={gallery} className="h-[128px] rounded-b-none border-0" />}
		{/* main content */}
		<div className="flex flex-col gap-1 p-3">
			{/* left col */}
			<header className="flex items-start justify-between gap-3">
				{/* title */}
				<Text size="md" c="lucky-point" className="text-xl">
					{title}
				</Text>

				{/* price */}
				<Text fw={700} size="md" c="lucky-point" className="text-xl">
					{price}
				</Text>
			</header>

			{/* right col */}
			<div className="flex justify-between gap-3">
				<div className="flex flex-col gap-2">
					{/* description */}
					{!isEmpty(description) && (
						<Text size="xs" c="dimmed" className="text-base">
							{description}
						</Text>
					)}

					{/* footer */}
					{isDefined(footer) && <div className="mt-auto">{footer}</div>}
				</div>

				{/* actions */}
				{isDefined(actions) && <div className="mt-auto">{actions}</div>}
			</div>
		</div>
	</div>
);

/**
 * ProductCardBase component props type
 * @export
 */
export type ProductCardBaseProps = ExtendedComponent<{
	title: React.ReactNode;
	description?: React.ReactNode;
	price: React.ReactNode;
	footer?: React.ReactNode;
	actions?: React.ReactNode;
	type?: CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE;
	gallery?: {
		// TODO: event gallery entity type
		galleryId: UUID;
		name: string;
		description?: string;
		images: Array<{
			imageId: UUID;
			name: string;
			description?: string;
			imageUrl: string;
		}>;
	};
}>;
